import type { PlayableWeaponId } from '../reference/weapon-progression-v0.js';
import type { CharacterRecord, QuarantinedSkill } from './model.js';
import type { CraftOptionView } from './skill-crafting.js';
import type { SkillSummaryView } from './skill-summary.js';
import type { GameFieldDescription } from './field.js';
import type { SkillVisualProfileV1 } from '../reference/skill-asset-specs-v1.js';

export interface AccountSummary { id: string; username: string }

export type ApiResult<T> = { ok: true; value: T } | { ok: false; error: { code: string; message: string } };
/**
 * A quarantined skill as it leaves the server: its envelope and why it is held. The held definition
 * stays in the store; only persistence and the boot re-check read it.
 */
export type QuarantinedSkillView = Omit<QuarantinedSkill, 'quarantine'> & { quarantine: { code: string; message: string; at: string; reasonLabel: string } };
/** A character as every response carries it. */
export type CharacterView = Omit<CharacterRecord, 'quarantinedSkills'> & { quarantinedSkills: QuarantinedSkillView[] };
/** `summaries`: one per owned skill, computed against the character as returned; never stored. */
export interface MeView {
  account: AccountSummary; character: CharacterView; summaries: readonly SkillSummaryView[];
  /** The server's dev-tools switch; the HUD shows the debug panel only when this is true. */
  devTools: boolean;
  /** Seconds left on this account's recall channel, or null when none is running. */
  recallRemainingS: number | null;
  /** Seconds until another recall may start, or null when none is pending. */
  recallCooldownRemainingS: number | null;
}
/**
 * Presentation profiles of every skill currently equipped in the room; rebuilt
 * per request. `conflictedProfileIds` are tombstones: ids whose skills disagree
 * on a profile, which clients must stop drawing even if cached earlier.
 */
export interface SkillVisualProfilesView { profiles: readonly SkillVisualProfileV1[]; conflictedProfileIds: readonly string[]; }
/** `craftableWeapons`: the weapons with at least one `AVAILABLE` source option in the craft catalog, decided by the server. */
export interface WorldView { field: GameFieldDescription; weapons: readonly PlayableWeaponId[]; craftableWeapons: readonly PlayableWeaponId[]; tickRate: number; }

/** The crafting catalog exactly as the browser receives it. */
export interface SkillOptionsView {
  options: readonly CraftOptionView[];
}

/**
 * A craft that produced a skill, plus the character it was stored on.
 *
 * `sacrificed` is reported rather than hidden: a skill the solver reached by
 * giving something up is still a success, but the player asked for the thing
 * that was dropped and is entitled to know it was.
 */
export interface SkillCraftView { character: CharacterView; skillId: string; sacrificed: boolean; summary: SkillSummaryView; summaries: readonly SkillSummaryView[]; }

/** The error code of a status-minimum search a newer request from the same account replaced. */
export const STATUS_MINIMUMS_SUPERSEDED_CODE = 'STATUS_MINIMUMS_SUPERSEDED';

/** The first craftable share and last element-compatible share for each status; a status with no craftable share is absent. */
export interface SkillStatusMinimumsView { minimums: Readonly<Record<string, { min: number; max: number }>>; }

/** What a craft with the same body would produce; nothing was saved. */
export interface SkillPreviewView { summary: SkillSummaryView; }
